/**
 * Hook to fetch real chart data for a Dashboard Indicator from the backend.
 *
 * The backend resolver returns a normalised envelope; the optional fields
 * present on the response depend on the indicator's chart_type. See
 * `backend/reporting/chart_resolver/` for per-type payload shapes.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import api from '../../../lib/api/client';

/** A single bar/line/pie row. Optional overlay keys (`sma`, `y2`...) are added by the resolver. */
export interface IndicatorDataPoint {
  name: string;
  value: number;
  target?: number;
  sma?: number;
  delta?: number;
  cumulative?: number;
  color?: string;
  [extraKey: string]: string | number | undefined;
}

export interface IndicatorSummaryMetric {
  value: string;
  label: string;
  change: string;
}

export interface IndicatorSeriesEntry {
  label: string;
  stats_type?: number;
  show_type?: number;
}

export interface IndicatorMeta {
  x_label?: string;
  y_label?: string;
  date_interval?: number | null;
  target_value?: number | null;
  show_sma?: boolean;
  show_total?: boolean;
  waterfall?: boolean;
  reason?: string;
  /** Drill-down URL template with `{value}` placeholder for chart segments. */
  drilldown_url?: string;
}

export interface IndicatorHeatmapPayload {
  x_categories: string[];
  y_categories: string[];
  cells: { x: string; y: string; value: number }[];
}

export interface IndicatorGeoPayload {
  points: { key: string; value: number }[];
  projection?: string;
}

export interface IndicatorGridColumn {
  key: string;
  label: string;
  type: string;
  dsid?: number;
  field_id?: number;
  drilldown_url?: string;
}

export interface IndicatorKpiPayload {
  value: number;
  label: string;
  unit?: string;
  target?: number | null;
  change?: number | null;
  stats_type_label?: string;
}

export interface IndicatorMarkupPayload {
  html: string;
  links: { label: string; url: string }[];
}

export interface IndicatorSummaryPayload {
  metrics: IndicatorSummaryMetric[];
  topic?: string;
}

export interface IndicatorDataResponse {
  indicator_id: number;
  indicator_name: string;
  chart_type: string;
  data: IndicatorDataPoint[];
  series?: Record<string, IndicatorSeriesEntry>;
  meta?: IndicatorMeta;
  summary?: IndicatorSummaryPayload | null;
  is_donut?: boolean;
  heatmap?: IndicatorHeatmapPayload;
  geo?: IndicatorGeoPayload;
  columns?: IndicatorGridColumn[];
  rows?: Record<string, unknown>[];
  total_count?: number;
  page_size?: number;
  kpi?: IndicatorKpiPayload;
  markup?: IndicatorMarkupPayload;
  message?: string;
  format_rules?: Array<{
    column: string;
    op: string;
    value: string | number;
    color: string;
    background?: string;
  }>;
}

/**
 * Context that supplies pre-resolved indicator payloads keyed by indicator id.
 *
 * Used by the public dashboard route so chart components can render without
 * making an authenticated `/reporting/indicators/{id}/data/` request — the
 * publish endpoint returns the resolved payloads in one shot, and we simply
 * hand them to the existing chart components.
 *
 * When the context provides an entry for a given indicator id, `useIndicatorData`
 * skips the fetch and returns it directly.
 */
export const IndicatorDataContext = createContext<Record<number, IndicatorDataResponse> | null>(null);

/** Ad-hoc cfg-shape filter accepted by the data endpoint's `filters` query param. */
export interface AdhocIndicatorFilter {
  field_dsid: number;
  field_id: number;
  operator: string;
  /** Scalar for text operators; `{start, end}` ISO pair for `exact_period`. */
  value: string | { start: string; end: string } | null;
  /** Supersede the indicator's own filters on this field instead of ANDing. */
  replace?: boolean;
}

/**
 * Fetches `/reporting/indicators/{id}/data/` and returns the resolver envelope
 * along with loading state. Re-fetches when `indicatorId` changes.
 *
 * If an `IndicatorDataContext` provider is mounted above and supplies a payload
 * for this indicator id, the fetch is skipped and the provided payload is
 * returned immediately. This is how the public dashboard renders without auth.
 *
 * @param indicatorId - DashboardIndicator primary key, or undefined to skip fetch
 * @param extraFilters - optional ad-hoc cfg-shape filters appended server-side
 *   (see the data action's `filters` query param). Pass a stable/memoised
 *   array; a change re-fetches. Ignored when a context preset supplies the
 *   payload — the provider is then responsible for applying the filters.
 * @param scopeParams - optional view-scope query params (`group` / `company` /
 *   `site`, as returned by `useView().getViewQueryParams()`). The resolver
 *   honours these ahead of the persisted view, so a page that passes them
 *   re-fetches the right slice the moment "View Data For" changes instead of
 *   showing the previous scope until reload.
 * @returns `{ data, loading }` — `data` is `null` until the first response arrives
 */
export function useIndicatorData(
  indicatorId: number | undefined,
  extraFilters?: AdhocIndicatorFilter[],
  scopeParams?: Record<string, number | undefined>,
) {
  const presetMap = useContext(IndicatorDataContext);
  const preset = indicatorId !== undefined && presetMap ? presetMap[indicatorId] : undefined;

  const [data, setData] = useState<IndicatorDataResponse | null>(preset || null);
  const [loading, setLoading] = useState(false);
  const filtersKey = extraFilters && extraFilters.length ? JSON.stringify(extraFilters) : '';
  const scopeKey = scopeParams && Object.keys(scopeParams).length ? JSON.stringify(scopeParams) : '';

  useEffect(() => {
    if (preset) {
      setData(preset);
      setLoading(false);
      return;
    }
    if (!indicatorId) return;
    let cancelled = false;
    setLoading(true);
    const params: Record<string, string | number> = {};
    if (filtersKey) params['filters'] = filtersKey;
    if (scopeKey) {
      const scope = JSON.parse(scopeKey) as Record<string, number | undefined>;
      Object.entries(scope).forEach(([k, v]) => { if (v !== undefined) params[k] = v; });
    }
    api.get(`/reporting/indicators/${indicatorId}/data/`, { params: Object.keys(params).length ? params : undefined })
      .then(res => { if (!cancelled) setData(res.data as IndicatorDataResponse); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [indicatorId, preset, filtersKey, scopeKey]);

  return { data, loading };
}
